import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManagement = () => {
  const { sessionToken } = useUnifiedAuth();
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);
  
  // Launch promotion ended
  const LAUNCH_PROMOTION = {
    active: false,
    discount: 0.50,
    duration: '6 months',
    limit: '100 providers',
    tiers: ['Basic', 'Pro']
  };
  
  const getPromotionalPrice = (tierName, originalPrice) => {
    if (LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tierName)) {
      return originalPrice * (1 - LAUNCH_PROMOTION.discount);
    }
    return originalPrice;
  };
  
  const hasPromotion = (tierName) => {
    return LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tierName);
  };

  useEffect(() => {
    fetchSubscriptionData();
    checkPaymentStatus();
  }, []);
  
  const checkPaymentStatus = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    
    if (paymentStatus) {
      if (paymentStatus === 'success') {
        alert('✅ Subscription upgraded successfully!');
      } else {
        alert('❌ Payment failed. Please try again.');
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/provider/subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setCurrentSubscription(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setLoading(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    if (!window.confirm('Upgrade your subscription?')) return;

    try {
      setUpgrading(true);
      const response = await axios.post(
        `${API}/provider/subscription/upgrade`,
        { tier_id: tierId, billing_cycle: selectedBillingCycle, payment_method: 'myfatoorah' },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.payment_required && response.data.payment_url) {
        window.location.href = response.data.payment_url;
        return;
      }
      
      alert(response.data.message);
      fetchSubscriptionData();
      setUpgrading(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to upgrade');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  const currentTier = currentSubscription?.current_tier;
  const usage = currentSubscription?.usage;

  // Tier data with features
  const tiers = [
    {
      name: 'Free',
      price: 0,
      commission: 15,
      color: 'from-slate-700 to-slate-800',
      borderColor: 'border-slate-600',
      features: [
        '5 staff members',
        '35 services',
        '1 branch',
        '3 months scheduling',
        'Unlimited bookings',
        'Basic marketing',
        'Email support'
      ]
    },
    {
      name: 'Basic',
      price: 20,
      commission: 12.5,
      color: 'from-sky-600 to-cyan-600',
      borderColor: 'border-sky-500',
      badge: hasPromotion('Basic') ? '50% OFF 🎉' : 'Popular',
      features: [
        '8 staff members',
        '60 services',
        '3 branches',
        '6 months scheduling',
        'Unlimited bookings',
        'Basic marketing package',
        'Email support'
      ]
    },
    {
      name: 'Pro',
      price: 60,
      commission: 10,
      color: 'from-purple-600 to-pink-600',
      borderColor: 'border-purple-500',
      badge: hasPromotion('Pro') ? '50% OFF 🎉' : 'Best Value',
      features: [
        '12 staff members',
        '75 services',
        '6 branches',
        '18 months scheduling',
        'Unlimited bookings',
        'Pro marketing package',
        'Priority support'
      ]
    },
    {
      name: 'Enterprise',
      price: null,
      isCustomPricing: true,
      commission: 5,
      color: 'from-amber-600 to-orange-600',
      borderColor: 'border-amber-500',
      badge: 'Premium',
      features: [
        '25-100 staff members',
        'Unlimited services',
        '10-20 branches',
        '36 months scheduling',
        'Unlimited bookings',
        'Premium marketing',
        'Dedicated support'
      ]
    }
  ];

  return (
    <section className="bg-slate-950 min-h-screen py-12 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        
        {/* Launch Promotion Banner */}
        {LAUNCH_PROMOTION.active && (
          <div className="mb-8 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 rounded-lg p-6 text-center border-2 border-yellow-400 shadow-2xl animate-pulse">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-4xl">🎉</span>
              <h3 className="text-2xl md:text-3xl font-black text-white">LAUNCH PROMOTION</h3>
              <span className="text-4xl">🎉</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-yellow-300 mb-2">
              50% OFF Basic & Pro Plans for 6 Months!
            </p>
            <p className="text-white text-sm md:text-base">
              Limited to the first 100 providers - Don't miss out! 🚀
            </p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Subscription Plans</h1>
          <p className="text-slate-300">Upgrade or manage your subscription</p>
        </div>

        {/* Current Plan Card */}
        {currentTier && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-xl p-6 mb-8 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Current Plan: {currentTier.tier_name}</h2>
                <p className="text-blue-100 mb-4">
                  {currentTier.tier_name === 'Free' 
                    ? 'You are on the Free plan' 
                    : `${currentTier.price_monthly} KD/month`}
                </p>
                
                {/* Usage Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-xs text-blue-100">Staff</p>
                    <p className="text-lg font-bold">{usage?.staff_count || 0} / {usage?.staff_limit || 0}</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-xs text-blue-100">Services</p>
                    <p className="text-lg font-bold">{usage?.service_count || 0} / {usage?.service_limit || 0}</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-xs text-blue-100">Branches</p>
                    <p className="text-lg font-bold">{usage?.branch_count || 0} / {usage?.branch_limit || 0}</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-xs text-blue-100">Commission</p>
                    <p className="text-lg font-bold">{currentTier.commission_rate ?? 15}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-900 rounded-lg p-1 inline-flex border border-slate-800">
            <button
              onClick={() => setSelectedBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                selectedBillingCycle === 'monthly'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedBillingCycle('annual')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors relative ${
                selectedBillingCycle === 'annual'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                Save 10%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tiers.map((tier) => {
            const isCurrentTier = currentTier?.tier_name === tier.name;
            const displayPrice = selectedBillingCycle === 'annual' ? tier.price * 10 : tier.price;
            const promoPrice = getPromotionalPrice(tier.name, tier.price);
            const displayPromoPrice = selectedBillingCycle === 'annual' ? promoPrice * 10 : promoPrice;

            return (
              <div
                key={tier.name}
                className={`rounded-2xl bg-slate-900/80 border-2 ${tier.borderColor} p-6 flex flex-col relative overflow-hidden transition-all hover:scale-105 ${
                  isCurrentTier ? 'ring-4 ring-yellow-400' : ''
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <span className={`absolute right-4 top-4 text-white text-xs px-3 py-1 rounded-full font-bold ${
                    hasPromotion(tier.name) 
                      ? 'bg-gradient-to-r from-pink-500 to-purple-500 animate-pulse'
                      : 'bg-sky-500/10 text-sky-200 border border-sky-500/30'
                  }`}>
                    {tier.badge}
                  </span>
                )}
                
                {isCurrentTier && (
                  <span className="absolute left-4 top-4 bg-yellow-400 text-slate-900 text-xs px-3 py-1 rounded-full font-bold">
                    CURRENT
                  </span>
                )}

                <h3 className="text-xl font-bold text-white mb-3 mt-8">{tier.name}</h3>
                
                {/* Price */}
                <div className="mb-4">
                  {hasPromotion(tier.name) && (
                    <p className="text-lg text-slate-400 line-through">
                      {selectedBillingCycle === 'annual' ? `${displayPrice} KWD` : `${tier.price} KWD`}
                    </p>
                  )}
                  <p className="text-3xl font-bold text-white">
                    {tier.isCustomPricing ? (
                      <>
                        Let's talk
                        <span className="text-sm text-slate-300 font-normal"> / custom quote</span>
                      </>
                    ) : (
                      <>
                        {hasPromotion(tier.name) ? displayPromoPrice : displayPrice} KWD
                        <span className="text-sm text-slate-300 font-normal"> 
                          / {selectedBillingCycle === 'annual' ? 'year' : 'mo'}
                        </span>
                      </>
                    )}
                  </p>
                  {!tier.isCustomPricing && selectedBillingCycle === 'annual' && tier.price > 0 && (
                    <p className="text-xs text-green-400 font-medium mt-1">2 months free</p>
                  )}
                  {hasPromotion(tier.name) && (
                    <p className="text-xs text-pink-400 font-bold mt-1">
                      🎁 First {LAUNCH_PROMOTION.duration} - Then regular price
                    </p>
                  )}
                </div>

                {/* Commission Badge */}
                <div className="mb-4">
                  <span className="inline-block bg-slate-800 text-slate-200 px-3 py-1 rounded text-xs">
                    {tier.commission}% commission
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1 text-sm text-slate-200">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>• {feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                {isCurrentTier ? (
                  <button
                    disabled
                    className="w-full bg-green-100 text-green-700 py-3 rounded-xl text-sm font-bold cursor-default"
                  >
                    ✓ Current Plan
                  </button>
                ) : tier.name === 'Free' ? (
                  <button
                    disabled
                    className="w-full bg-slate-700 text-slate-400 py-3 rounded-xl text-sm font-medium cursor-not-allowed"
                  >
                    Free Tier
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(tier.name.toLowerCase())}
                    disabled={upgrading}
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white py-3 rounded-xl text-sm font-bold transition disabled:opacity-50"
                  >
                    {upgrading ? 'Processing...' : 'Upgrade Now'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Need help choosing?</h2>
          <p className="text-slate-300 mb-6">
            Our subscription plans are designed to grow with your practice. Start with Free and upgrade as your needs expand.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-slate-300">
            <div>
              <h3 className="font-bold text-white mb-2">📞 Contact Sales</h3>
              <p className="text-sm">Have questions? Our team is here to help you choose the right plan.</p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">💳 Secure Payments</h3>
              <p className="text-sm">All payments are processed securely through MyFatoorah.</p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">🔄 Easy Upgrades</h3>
              <p className="text-sm">Upgrade or downgrade anytime. No long-term commitments.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionManagement;